import { isValueNull } from "@1823-partners/core";
import config from "../data/config.json";
import creFields from "../data/creFields.json";

// Re-export isValueNull from core for consumers that import from creUtils
export { isValueNull };

/**
 * Formats CRE loan data for submission to the API
 * Only includes fields that exist in the CRE table definition
 * @param {Object} loan - The CRE loan data to format
 * @returns {Object} - The formatted loan data with only table fields
 */
export const formatCREData = (loan) => {
  // Get allowed fields from creFields (excluding UI-only and calculated fields)
  const allowedFields = Object.keys(creFields).filter(
    key => key !== 'EDIT' && !creFields[key].calculated
  );

  // Filter to only include allowed fields
  const formattedLoan = {};
  allowedFields.forEach(field => {
    if (loan.hasOwnProperty(field)) {
      formattedLoan[field] = loan[field];
    }
  });

  // Convert empty string fields to null (except booleans which should be false/null)
  Object.keys(formattedLoan).forEach(key => {
    if (!config.booleanFields.includes(key) && formattedLoan[key] === '') {
      formattedLoan[key] = null;
    }
  });

  return formattedLoan;
};

/**
 * Validates CRE loan data against required fields
 * @param {Object} loanData - The CRE loan data to validate
 * @param {boolean} isNewEntry - Whether this is a new entry (affects CUSIP validation)
 * @returns {Array} - Array of missing required field names
 */
export const validateCREData = (loanData, isNewEntry = false) => {
  const emptyFields = config.validation.requiredFields.filter(field => {
    // Special handling for CUSIP - if it's a new entry, it's OK to be empty
    if (field === 'CUSIP' && isNewEntry) {
      return false;
    }
    return isValueNull(loanData[field]);
  });

  return emptyFields;
};

/**
 * Validates a list of CRE loan entries and returns validation errors
 * @param {Array} entries - Array of CRE loan entry objects to validate
 * @returns {Array} - Array of validation error objects with row, cusip, loanName, and emptyFields
 */
export const validateCREList = (entries) => {
  const errorEntries = [];
  entries.forEach((entry, idx) => {
    const emptyFields = config.validation.requiredFields.filter(col => isValueNull(entry[col]));
    if (emptyFields.length !== 0) {
      errorEntries.push({
        row: idx,
        cusip: entry['CUSIP'],
        loanName: entry['LOAN_NAME'],
        emptyFields: emptyFields
      });
    }
  });

  return errorEntries;
};

/**
 * Gets the display name for a field from creFields
 * @param {string} fieldKey - The field key
 * @returns {string} - The display name or the field key if not found
 */
export const getFieldDisplayName = (fieldKey) => {
  return creFields[fieldKey]?.name || fieldKey;
};

/**
 * Gets the choices for a field from creFields
 * @param {string} fieldKey - The field key
 * @param {string} dependentValue - Optional dependent value for cascading dropdowns
 * @returns {Array} - Array of choices or empty array if not found
 */
export const getFieldChoices = (fieldKey, dependentValue = null) => {
  const fieldConfig = creFields[fieldKey];
  if (!fieldConfig || !fieldConfig.choices) {
    return [];
  }

  // Handle cascading dropdowns (like PROPERTY_SUBTYPE depending on PROPERTY_TYPE)
  if (typeof fieldConfig.choices === 'object' && !Array.isArray(fieldConfig.choices)) {
    if (dependentValue && fieldConfig.choices[dependentValue]) {
      return fieldConfig.choices[dependentValue];
    }
    // Return all possible values if no dependent value specified
    return Object.values(fieldConfig.choices).flat();
  }

  return fieldConfig.choices;
};

/**
 * Gets dropdown choices for a specific grid row, including a legacy current
 * value when it is not present in the latest configured choices.
 * @param {string} fieldKey - The field key
 * @param {Object} row - The grid row
 * @returns {Array} - Choices applicable to the row
 */
export const getRowFieldChoices = (fieldKey, row = {}) => {
  const fieldConfig = creFields[fieldKey];
  const dependentValue = fieldConfig?.dependsOn ? row[fieldConfig.dependsOn] : null;
  const isDependentChoices =
    fieldConfig?.choices && typeof fieldConfig.choices === 'object' && !Array.isArray(fieldConfig.choices);
  const choices = isDependentChoices
    ? (dependentValue && fieldConfig.choices[dependentValue] ? fieldConfig.choices[dependentValue] : [])
    : getFieldChoices(fieldKey, dependentValue);
  const currentValue = row[fieldKey];

  if (
    currentValue !== null &&
    currentValue !== undefined &&
    currentValue !== '' &&
    !choices.includes(currentValue)
  ) {
    return [currentValue, ...choices];
  }

  return choices;
};

/**
 * Gets the user-facing label for a stored dropdown value.
 * Fields without explicit labels continue to display their stored value.
 * @param {string} fieldKey - The field key
 * @param {*} value - The stored dropdown value
 * @returns {*} - The configured label or the original value
 */
export const getChoiceDisplayLabel = (fieldKey, value) => {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  return creFields[fieldKey]?.choiceLabels?.[String(value)] || value;
};

/**
 * Checks if a field is editable
 * @param {string} fieldKey - The field key
 * @returns {boolean} - True if the field is editable
 */
export const isFieldEditable = (fieldKey) => {
  return creFields[fieldKey]?.editable !== false;
};

/**
 * Gets the data type for a field
 * @param {string} fieldKey - The field key
 * @returns {string} - The data type or 'string' as default
 */
export const getFieldDataType = (fieldKey) => {
  return creFields[fieldKey]?.datatype || 'string';
};

/**
 * Gets all fields for a specific category
 * @param {string} category - The category name
 * @returns {Array} - Array of field keys in the category
 */
export const getFieldsByCategory = (category) => {
  return Object.entries(creFields)
    .filter(([key, field]) => field.category === category)
    .map(([key]) => key);
};

/**
 * Formats validation errors from validateCREList into display strings
 * for use with 1823-core's ValidationErrorModal
 * @param {Array} errors - Array of error objects from validateCREList
 * @returns {Array} - Array of error strings suitable for 1823-core ValidationErrorModal
 */
export const formatValidationErrorsForDisplay = (errors) => {
  return errors.map(error => {
    const identifier = error.loanName || error.cusip || 'Unknown';
    const fieldNames = error.emptyFields.map(field => getFieldDisplayName(field)).join(', ');
    return `Row ${error.row + 1} (${identifier}): Missing required fields - ${fieldNames}`;
  });
};

/**
 * Formats a numeric value based on field configuration
 * @param {*} value - The value to format
 * @param {string} fieldKey - The field key
 * @returns {string} - The formatted value
 */
export const formatNumericValue = (value, fieldKey) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const fieldConfig = creFields[fieldKey];
  const subtype = fieldConfig?.subtype;

  if (subtype === 'percent') {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return (numValue * 100).toFixed(2) + '%';
    }
  }

  if (subtype === 'int') {
    return Math.round(parseFloat(value)).toString();
  }

  if (subtype === 'no-commas') {
    return value.toString();
  }

  // Default numeric formatting with commas
  const numValue = parseFloat(value);
  if (!isNaN(numValue)) {
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  return value.toString();
};

// ============================================================================
// Enrichment Utilities - Client-side data merging
// ============================================================================

/**
 * Normalizes a ZIP code for comparison
 * Handles: numeric values, trailing .0, leading zeros, ZIP+4 format
 * @param {*} zip - The ZIP code to normalize
 * @returns {string} - Normalized ZIP code
 */
const normalizeZipCode = (zip) => {
  if (zip === null || zip === undefined || zip === '') {
    return '';
  }

  let normalized = String(zip);

  // Remove trailing .0 (from numeric conversion)
  normalized = normalized.replace(/\.0$/, '');

  // Remove ZIP+4 suffix (-XXXX)
  normalized = normalized.replace(/-\d{4}$/, '');

  // Remove leading zeros for numeric comparison
  // But preserve if it results in a valid 5-digit ZIP
  const numericPart = parseInt(normalized, 10);
  if (!isNaN(numericPart)) {
    normalized = String(numericPart);
  }

  return normalized;
};

/**
 * Merges location data into a loan entry by ZIP_CODE
 * @param {Object} loan - The loan entry to enrich
 * @param {Array} locations - Array of location records
 * @returns {Object} - The loan with location data merged
 */
export const mergeLocationData = (loan, locations) => {
  if (!loan.ZIP_CODE || !locations || locations.length === 0) {
    return loan;
  }

  // Normalize ZIP code for matching
  const normalizedLoanZip = normalizeZipCode(loan.ZIP_CODE);

  const locationMatch = locations.find(loc =>
    normalizeZipCode(loc.ZIP_CODE) === normalizedLoanZip
  );

  if (!locationMatch) {
    return loan;
  }

  // Merge location fields (don't override existing values)
  const locationFields = ['CITY', 'STATE_ID', 'CSA', 'MARKET', 'COUNTY', 'COUNTRY', 'NCREIF_REGION', 'ACLI_REGION'];
  const enriched = { ...loan };

  locationFields.forEach(field => {
    if (locationMatch[field] !== undefined && locationMatch[field] !== null) {
      // Only set if loan doesn't already have a value
      if (enriched[field] === undefined || enriched[field] === null || enriched[field] === '') {
        enriched[field] = locationMatch[field];
      }
    }
  });

  return enriched;
};

/**
 * Merges ClearWater market data into a loan entry by CUSIP
 * ClearWater values take precedence over existing values
 * @param {Object} loan - The loan entry to enrich
 * @param {Array} clearwater - Array of ClearWater records
 * @returns {Object} - The loan with ClearWater data merged
 */
export const mergeClearWaterData = (loan, clearwater) => {
  if (!loan.CUSIP || !clearwater || clearwater.length === 0) {
    return loan;
  }

  const cusipStr = String(loan.CUSIP);
  // Primary join: direct CUSIP match. Fallback: ClearWater records whose ID
  // is wrapped as 43960-<CUSIP>-MLS for the same underlying loan.
  const fallbackKey = `43960-${cusipStr}-MLS`;
  const cwMatch =
    clearwater.find(cw => String(cw.CUSIP) === cusipStr) ||
    clearwater.find(cw => String(cw.CUSIP) === fallbackKey);

  if (!cwMatch) {
    return loan;
  }

  // ClearWater fields take precedence when present
  const cwFields = [
    'INTEREST_RATE', 'SPREAD', 'MARKET_VALUE', 'MARKET_PRICE',
    'CURRENT_BALANCE', 'CARRYING_VALUE', 'INTEREST_TYPE', 'MATURITY',
    'AMORTIZATION_DESCRIPTION', 'PAYMENT_FREQUENCY', 'DAY_COUNT', 'CURRENCY'
  ];

  const enriched = { ...loan };

  cwFields.forEach(field => {
    if (cwMatch[field] !== undefined && cwMatch[field] !== null) {
      // ClearWater values override existing values
      enriched[field] = cwMatch[field];
    }
  });

  return enriched;
};

/**
 * Applies financial calculations to a loan entry
 * @param {Object} loan - The loan entry to calculate
 * @param {Array} npn - NCREIF index data
 * @param {Array} allLoans - All loans (used for B Note A+B balance lookup)
 * @returns {Object} - The loan with calculated fields
 */
const LOAN_STRATEGY_BY_CATEGORY = {
  'Core': 'Stabilized',
  'Pre-Stabilized': 'Transitional',
  'Light Value Add': 'Transitional',
  'Value Add': 'Transitional',
  'Construction/Opportunistic': 'Construction Opportunistic',
};

export const applyCalculations = (loan, npn = [], allLoans = []) => {
  const calculated = { ...loan };

  // Convert values to numbers
  const parsedCurrentYearNOI = parseFloat(loan.CURRENT_YEAR_NOI);
  const isCurrentYearNOIPresent =
    loan.CURRENT_YEAR_NOI !== null &&
    loan.CURRENT_YEAR_NOI !== undefined &&
    loan.CURRENT_YEAR_NOI !== '' &&
    !isNaN(parsedCurrentYearNOI);
  const currentYearNOI = isCurrentYearNOIPresent ? parsedCurrentYearNOI : 0;
  const debtService = parseFloat(loan.DEBT_SERVICE) || 0;
  const currentBalance = parseFloat(loan.CURRENT_BALANCE) || 0;
  const interestRate = parseFloat(loan.INTEREST_RATE) || 0;
  const annualServicingFee = parseFloat(loan.ANNUAL_SERVICING_FEE) || 0;
  const participation = parseFloat(loan.PARTICIPATION) || 1;
  const asIsAppraisedValue = parseFloat(loan.AS_IS_APPRAISED_VALUE) || 0;
  const priorYearNOI = parseFloat(loan.PRIOR_YEAR_NOI) || 0;
  const secondPriorYearNOI = parseFloat(loan.SECOND_PRIOR_YEAR_NOI) || 0;

  // DSCR = Current Year NOI / Debt Service (leave blank when CURRENT_YEAR_NOI is missing)
  if (isCurrentYearNOIPresent && debtService !== 0) {
    calculated.DSCR = currentYearNOI / debtService;
  } else {
    delete calculated.DSCR;
  }

  // Debt Yield = Current Year NOI / Current Balance (leave blank when CURRENT_YEAR_NOI is missing)
  if (isCurrentYearNOIPresent && currentBalance !== 0) {
    calculated.DEBT_YIELD = currentYearNOI / currentBalance;
  } else {
    delete calculated.DEBT_YIELD;
  }

  // Net Interest Rate = Interest Rate - Annual Servicing Fee
  calculated.NET_INT_RATE = interestRate - annualServicingFee;

  // Current Balance LTV = (Current Balance / Participation) / As-Is Appraised Value
  if (asIsAppraisedValue !== 0 && participation !== 0) {
    calculated.CUR_BAL_LTV = (currentBalance / participation) / asIsAppraisedValue;
  } else {
    delete calculated.CUR_BAL_LTV;
  }

  // RBC DSCR with 25-year amortization
  // NOI weighting based on years of data available:
  //   3 years: 50% current + 30% prior + 20% second prior
  //   2 years: 65% current + 35% prior
  //   1 year:  100% current
  // For B Notes: use combined A Note + B Note balance (matched by LOAN_NAME)
  delete calculated.RBC_DSCR;
  if (isCurrentYearNOIPresent && interestRate > 0 && currentBalance !== 0) {
    let rbcBalance = currentBalance;
    if (loan.LOAN_TYPE === 'B Note' && loan.LOAN_NAME && allLoans.length > 0) {
      const aNote = allLoans.find(
        l => l.LOAN_TYPE === 'A Note' && l.LOAN_NAME === loan.LOAN_NAME
      );
      if (aNote) {
        rbcBalance += (parseFloat(aNote.CURRENT_BALANCE) || 0);
      }
    }

    const monthlyRate = interestRate / 100 / 12;
    const nper = 300; // 25 years * 12 months
    const pmt = monthlyRate !== 0
      ? rbcBalance * (monthlyRate * Math.pow(1 + monthlyRate, nper)) / (Math.pow(1 + monthlyRate, nper) - 1) * 12
      : 0;
    if (pmt !== 0) {
      const hasCurrentNOI = loan.CURRENT_YEAR_NOI != null && loan.CURRENT_YEAR_NOI !== '' && currentYearNOI !== 0;
      const hasPriorNOI = loan.PRIOR_YEAR_NOI != null && loan.PRIOR_YEAR_NOI !== '' && priorYearNOI !== 0;
      const hasSecondPriorNOI = loan.SECOND_PRIOR_YEAR_NOI != null && loan.SECOND_PRIOR_YEAR_NOI !== '' && secondPriorYearNOI !== 0;

      let avgNOI;
      if (hasCurrentNOI && hasPriorNOI && hasSecondPriorNOI) {
        avgNOI = (currentYearNOI * 0.5) + (priorYearNOI * 0.3) + (secondPriorYearNOI * 0.2);
      } else if (hasCurrentNOI && hasPriorNOI) {
        avgNOI = (currentYearNOI * 0.65) + (priorYearNOI * 0.35);
      } else {
        avgNOI = currentYearNOI;
      }

      calculated.RBC_DSCR = avgNOI / pmt;
    }
  }

  // Maturity Year (use UTC calendar year so YYYY-MM-DD strings do not shift west of UTC)
  if (loan.MATURITY) {
    const maturityDate = new Date(loan.MATURITY);
    if (!isNaN(maturityDate.getTime())) {
      calculated.MATURITY_YEAR = maturityDate.getUTCFullYear();
    } else {
      delete calculated.MATURITY_YEAR;
    }
  } else {
    delete calculated.MATURITY_YEAR;
  }

  // Remaining Term (months until fully extended maturity; UTC calendar parts for date-only input)
  if (loan.FULLY_EXTENDED_MATURITY_DATE) {
    const matDate = new Date(loan.FULLY_EXTENDED_MATURITY_DATE);
    const today = new Date();
    if (!isNaN(matDate.getTime())) {
      const monthDiff = (matDate.getUTCFullYear() - today.getUTCFullYear()) * 12 +
        (matDate.getUTCMonth() - today.getUTCMonth());
      calculated.REM_TERM = monthDiff;
    } else {
      delete calculated.REM_TERM;
    }
  } else {
    delete calculated.REM_TERM;
  }

  // Property Measure Unit mapping
  const unitMap = {
    'Apts/Residential': 'Units',
    'Retail': 'SF',
    'Office': 'SF',
    'Industrial': 'SF',
    'Hospitality': 'Keys',
    'Self-Storage': 'SF',
    'Other': 'Other'
  };
  if (loan.PROPERTY_TYPE) {
    calculated.PROPERTY_MEASURE_UNIT = unitMap[loan.PROPERTY_TYPE] || 'Other';
  }

  // Loan Strategy classification
  calculated.LOAN_STRATEGY = LOAN_STRATEGY_BY_CATEGORY[loan.LOAN_CATEGORY] || 'Transitional';

  // RBC NCREIF and RBC LTV using Bloomberg NCREIF index
  delete calculated.RBC_NCREIF;
  delete calculated.RBC_LTV;
  if (npn.length > 0 && loan.ORIGINATION_DATE && asIsAppraisedValue !== 0) {
    const origDate = new Date(loan.ORIGINATION_DATE);
    if (!isNaN(origDate.getTime())) {
      const origQuarter = Math.floor(origDate.getUTCMonth() / 3) + 1;
      const origYear = origDate.getUTCFullYear();

      // Find the latest NPNCRE value
      let latestNpncre = null;
      let latestDate = null;
      npn.forEach(rec => {
        const d = new Date(rec.REPORTING_DATE);
        if (!isNaN(d.getTime()) && (latestDate === null || d > latestDate)) {
          latestDate = d;
          latestNpncre = rec.NPNCRE;
        }
      });

      // Find the origination quarter's NPNCRE value
      const origMatch = npn.find(rec => {
        const d = new Date(rec.REPORTING_DATE);
        if (isNaN(d.getTime())) return false;
        return d.getUTCFullYear() === origYear && Math.floor(d.getUTCMonth() / 3) + 1 === origQuarter;
      });

      if (latestNpncre != null && origMatch && origMatch.NPNCRE && origMatch.NPNCRE !== 0) {
        calculated.RBC_NCREIF = (1 + (latestNpncre - origMatch.NPNCRE) / origMatch.NPNCRE) * asIsAppraisedValue;
      } else {
        calculated.RBC_NCREIF = asIsAppraisedValue;
      }

      if (calculated.RBC_NCREIF && calculated.RBC_NCREIF !== 0) {
        calculated.RBC_LTV = currentBalance / calculated.RBC_NCREIF;
      }
    } else {
      // Invalid date parse — fall back to simple ratio
      calculated.RBC_NCREIF = asIsAppraisedValue;
      calculated.RBC_LTV = currentBalance / asIsAppraisedValue;
    }
  } else if (asIsAppraisedValue !== 0) {
    // Fallback: no NPN data, use appraised value directly
    calculated.RBC_NCREIF = asIsAppraisedValue;
    calculated.RBC_LTV = currentBalance / asIsAppraisedValue;
  }

  // Amortization Description mapping
  if (!loan.AMORTIZATION_DESCRIPTION && loan.AMORTIZATION_TYPE) {
    const amortMap = {
      1: 'Fully Amortizing',
      2: 'Amortizing with Balloon',
      3: 'Full IO',
      4: 'Partial IO, then Amortizing'
    };
    calculated.AMORTIZATION_DESCRIPTION = amortMap[loan.AMORTIZATION_TYPE] || 'Unknown';
  }

  return calculated;
};

/**
 * Fully enriches a loan entry with all available data
 * @param {Object} loan - The loan entry to enrich
 * @param {Object} enrichmentData - Object containing locations, clearwater, and npn arrays
 * @param {Array} allLoans - All loans (used for B Note A+B balance lookup)
 * @returns {Object} - The fully enriched loan
 */
export const enrichLoan = (loan, enrichmentData, allLoans = []) => {
  if (!enrichmentData) {
    return loan;
  }

  const { locations, clearwater, npn } = enrichmentData;

  // Apply enrichments in order
  let enriched = { ...loan };

  // 1. Merge location data (by ZIP_CODE)
  enriched = mergeLocationData(enriched, locations);

  // 2. Merge ClearWater data (by CUSIP, takes precedence)
  enriched = mergeClearWaterData(enriched, clearwater);

  // 3. Apply calculations
  enriched = applyCalculations(enriched, npn, allLoans);

  return enriched;
};

/**
 * Enriches multiple loan entries
 * Uses optimized Maps for O(1) lookups when processing many rows
 * @param {Array} loans - Array of loan entries
 * @param {Object} enrichmentData - Object containing locations, clearwater, and npn arrays
 * @returns {Array} - Array of enriched loans
 */
export const enrichLoans = (loans, enrichmentData) => {
  if (!loans || !Array.isArray(loans)) {
    return [];
  }

  if (!enrichmentData) {
    return loans;
  }

  const { locations, clearwater, npn } = enrichmentData;

  // Build lookup Maps for O(1) access (much faster for many rows)
  const locationMap = new Map();
  if (locations && locations.length > 0) {
    locations.forEach(loc => {
      // Use normalized ZIP code as key
      const key = normalizeZipCode(loc.ZIP_CODE);
      locationMap.set(key, loc);
    });
  }

  const clearwaterMap = new Map();
  if (clearwater && clearwater.length > 0) {
    clearwater.forEach(cw => {
      clearwaterMap.set(String(cw.CUSIP), cw);
    });
  }

  // Use optimized enrichment with pre-built Maps
  return loans.map(loan => enrichLoanOptimized(loan, locationMap, clearwaterMap, npn, loans));
};

/**
 * Optimized loan enrichment using pre-built Maps
 * @private
 */
const enrichLoanOptimized = (loan, locationMap, clearwaterMap, npn, allLoans = []) => {
  let enriched = { ...loan };

  // 1. Merge location data (by ZIP_CODE) using Map lookup
  if (loan.ZIP_CODE && locationMap.size > 0) {
    const normalizedZip = normalizeZipCode(loan.ZIP_CODE);
    const locationMatch = locationMap.get(normalizedZip);

    if (locationMatch) {
      const locationFields = ['CITY', 'STATE_ID', 'CSA', 'MARKET', 'COUNTY', 'COUNTRY', 'NCREIF_REGION', 'ACLI_REGION'];
      locationFields.forEach(field => {
        if (locationMatch[field] !== undefined && locationMatch[field] !== null) {
          if (enriched[field] === undefined || enriched[field] === null || enriched[field] === '') {
            enriched[field] = locationMatch[field];
          }
        }
      });
    }
  }

  // 2. Merge ClearWater data (by CUSIP) using Map lookup. Fallback to
  // ClearWater records keyed as 43960-<CUSIP>-MLS when a direct match is missing.
  if (loan.CUSIP && clearwaterMap.size > 0) {
    const cusipStr = String(loan.CUSIP);
    const cwMatch =
      clearwaterMap.get(cusipStr) ||
      clearwaterMap.get(`43960-${cusipStr}-MLS`);

    if (cwMatch) {
      const cwFields = [
        'INTEREST_RATE', 'SPREAD', 'MARKET_VALUE', 'MARKET_PRICE',
        'CURRENT_BALANCE', 'CARRYING_VALUE', 'INTEREST_TYPE', 'MATURITY',
        'AMORTIZATION_DESCRIPTION', 'PAYMENT_FREQUENCY', 'DAY_COUNT', 'CURRENCY'
      ];
      cwFields.forEach(field => {
        if (cwMatch[field] !== undefined && cwMatch[field] !== null) {
          enriched[field] = cwMatch[field];
        }
      });
    }
  }

  // 3. Apply calculations
  enriched = applyCalculations(enriched, npn, allLoans);

  return enriched;
};
